import type { Location } from "react-router-dom";
import { buildReturnPath, canStorePostLoginRedirect } from "@/utils/postLoginRedirect";

export type AuthMode = "login" | "signup" | "reset";

export interface BuildAuthHrefOptions {
  mode?: AuthMode;
  signupType?: "company";
  redirect?: string | null;
  /** Referral code — maps to `?ref=` on /auth (not named `ref`; reserved in React). */
  referralCode?: string;
}

/**
 * Build an /auth URL with optional mode, signup type, referral code, and safe return path.
 */
export function buildAuthHref(options: BuildAuthHrefOptions = {}): string {
  const params = new URLSearchParams();
  if (options.mode && options.mode !== "login") {
    params.set("mode", options.mode);
  }
  if (options.signupType) {
    params.set("type", options.signupType);
  }
  if (options.referralCode) {
    params.set("ref", options.referralCode);
  }
  if (options.redirect && canStorePostLoginRedirect(options.redirect)) {
    params.set("redirect", options.redirect);
  }
  const query = params.toString();
  return query ? `/auth?${query}` : "/auth";
}

/** Derive a safe post-login redirect from the current route. */
export function getRedirectFromLocation(location: Location): string | undefined {
  const path = buildReturnPath(location.pathname, location.search, location.hash);
  return canStorePostLoginRedirect(path) ? path : undefined;
}

export function buildAuthHrefFromLocation(
  location: Location,
  options: Omit<BuildAuthHrefOptions, "redirect"> = {}
): string {
  return buildAuthHref({
    ...options,
    redirect: getRedirectFromLocation(location),
  });
}

/** Login path with ?redirect= for the current page (URL-visible return path). */
export function getAuthLoginPathFromLocation(location: Location): string {
  return buildAuthHrefFromLocation(location, { mode: "login" });
}
